import axios, { AxiosProgressEvent } from 'axios';

import { Common } from '../common';
import {
  Theme,
  CreateValues,
  PracticeTheme,
  UpdateValues,
} from '../models/practice/theme';

interface Entity {
  id: string;
  name: string;
}

interface NotInheritingEntities {
  practices: Entity[];
  practiceGroups?: Entity[];
}

interface InheritingEntity {
  inheritanceOwner: string;
  inheritanceOwnerId: string;
}

export class API {
  common: Common;

  constructor(common: Common) {
    this.common = common;
  }

  async findOneByPracticeId(practiceId: string): Promise<PracticeTheme> {
    const url = this.common.constructPath(`/practice-themes`, {
      practiceId,
    });
    return this.common.getJSON(url);
  }

  async findOneByPracticeGroupId(
    practiceGroupId: string,
  ): Promise<PracticeTheme> {
    const url = this.common.constructPath(`/practice-themes`, {
      practiceGroupId,
    });
    return this.common.getJSON(url);
  }

  async findOneByOrganizationId(
    organizationId: string,
  ): Promise<PracticeTheme> {
    const url = this.common.constructPath(`/practice-themes`, {
      organizationId,
    });
    return this.common.getJSON(url);
  }

  async findThemeByPracticeId(practiceId: string): Promise<Theme> {
    const url = this.common.constructPath(`/practice/${practiceId}/theme`);
    return this.common.getJSON(url);
  }

  async findThemeByOrganizationId(organizationId: string): Promise<Theme> {
    const url = this.common.constructPath(
      `/organizations/${organizationId}/theme`,
    );
    return this.common.getJSON(url);
  }

  async findThemeByPracticeGroupId(practiceGroupId: string): Promise<Theme> {
    const url = this.common.constructPath(
      `/practice-groups/${practiceGroupId}/theme`,
    );
    return this.common.getJSON(url);
  }

  async findInheritanceOwnerByPracticeIdAndAttribute(
    practiceId: string,
    attribute: string,
  ): Promise<InheritingEntity> {
    const url = this.common.constructPath(
      `/practice/${practiceId}/theme/inheritance-owner`,
      {
        attribute,
      },
    );
    return this.common.getJSON(url);
  }

  async findInheritanceOwnerByPracticeGroupIdAndAttribute(
    practiceGroupId: string,
    attribute: string,
  ): Promise<InheritingEntity> {
    const url = this.common.constructPath(
      `/practice-groups/${practiceGroupId}/theme/inheritance-owner`,
      {
        attribute,
      },
    );
    return this.common.getJSON(url);
  }

  async findNotIheritingEntitiesByOrganizationId(
    organizationId: string,
    attribute: string,
  ): Promise<NotInheritingEntities> {
    const url = this.common.constructPath(
      `/organizations/${organizationId}/theme/not-inheriting-entities`,
      {
        attribute,
      },
    );
    return this.common.getJSON(url);
  }

  async findNotIheritingEntitiesByPracticeGroupId(
    practiceGroupId: string,
    attribute: string,
  ): Promise<NotInheritingEntities> {
    const url = this.common.constructPath(
      `/practice-groups/${practiceGroupId}/theme/not-inheriting-entities`,
      {
        attribute,
      },
    );
    return this.common.getJSON(url);
  }

  async create(values: CreateValues): Promise<PracticeTheme> {
    const url = this.common.constructPath(`/practice-themes`);
    return this.common.postJSON(url, values);
  }

  async update(id: string, values: UpdateValues): Promise<PracticeTheme> {
    const url = this.common.constructPath(`/practice-themes/${id}`);
    return this.common.patchJSON(url, values);
  }

  async getSignedUploadURL(
    practiceId: string | undefined,
    practiceGroupId: string | undefined,
    organizationId: string | undefined,
    attribute: keyof Theme,
  ): Promise<string> {
    const url = this.common.constructPath(`/practice-themes/upload-url`);
    const response = await this.common.postJSON(url, {
      practiceId,
      practiceGroupId,
      organizationId,
      attribute,
    });
    return (response && response.url) || '';
  }

  async upload(
    file: File,
    signedUploadURL: string,
    onUploadProgress: (event: AxiosProgressEvent) => void,
  ): Promise<number> {
    const response = await axios.put(signedUploadURL, file, {
      onUploadProgress,
      headers: { 'Content-Type': 'image/png' },
    });
    return response.status;
  }
}
